import * as THREE from 'three';
import { cabinetLayout, hexToWorld, lecternLayout, sootLayout, spokeState, type BattleState, type CabinetLayout, type LecternLayout, type MapDef, type SootLayout } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The burnt library of the Order and the stilts of the fin (docs/sendas/mago.md, chapter 1), for the mage's path.
 *
 * - `sootCellars` (act 1): four halls of stone black with soot, the shelves along the walls charred (books fallen in
 *   heaps, some still smouldering, an ember glow low in them), a fallen shelf across each hall; piles of ash and bones
 *   where the risen sleep (flattened once they rise); the charred beams that drop over a stair while one stands; the
 *   collapse of beams at the way in; at the last hall's end the catalog, a wall of bronze drawers. The page in the
 *   hero's hand glows red by how near the nearest nightmare is.
 * - `lecterns` (act 2): a round reading room, its shelves; three iron lecterns, each with its sealed book (frost: a
 *   shell of blue ice; crust: a green crust that smokes; ink: black, wound in glowing red threads) and before it an arc of
 *   a frost bowl, an acid flask and a brazier. A seal pulses the colour of what it lacks, changes when readied, bursts
 *   open broken (the book's leaves rise); the things gone while they come back.
 * - `fireRoom` (act 3, the test): the room where the fire began; Ansel's iron cabinet in its middle with its three seals,
 *   three shelves by the wall with their things, the six spokes of floor where the walls of fire rise (red embers in the
 *   floor while warned, then tall flames), a door in each other wedge. All the embers go out when it opens.
 * - The scenes: `stilts` (the Terca at the stilts by night, Isra at the gunwale with the glowing page), `pier` (the same
 *   pier at dawn, a crate), `labYard` (the bastion's yard at dusk by the Laboratory: a table of flasks, a brazier),
 *   `catalog` (the wall of drawers, one clean), `collapse` (the collapse's mouth, a lantern, rubble), `fireOut` (the fire
 *   room after: the cabinet open, every ember out but the ink).
 *
 * Draw calls: the dressing (one merged mesh), the sea (stilts and pier), the piles, beams, seals, items and the
 * cabinet's doors (a handful of small groups), the flames and the red floor (two instanced meshes), the glow layer, the
 * sky. No lights of its own (the board's are repainted: HOURS); every lamp and ember is a glow sprite.
 */

export const BIBLIO_PLACES = new Set(['sootCellars', 'lecterns', 'fireRoom', 'stilts', 'pier', 'labYard', 'catalog', 'collapse', 'fireOut']);

const S = WORLD_PER_HEX;
const RED = new THREE.Color('#ff4f3a');
const EMBER = new THREE.Color('#ff6a2a');
const COLD = new THREE.Color('#dce8ff');
const FROSTC = new THREE.Color('#8cc4ff');
const ACIDC = new THREE.Color('#7dff8a');
const FIREC = new THREE.Color('#ff8a3a');
const INKC = new THREE.Color('#ff3a2a');
const SOOT = ['#383a40', '#41444b', '#4a4d55', '#323439'];
const FLAG = ['#4a4c51', '#53555a', '#44464b'];
const FLAG_ASH = ['#6e7279', '#767a81', '#686c73'];
const CHAR = ['#241e1a', '#2e2620', '#1c1815'];
const BOOKS = ['#5a2c26', '#2c3d48', '#40452e', '#3b2c3e', '#4f4a44', '#262a2e'];
const IRON = '#2c3036';
const BRONZE = '#6d5a48';

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

const HOURS: Record<
  Hour | 'inside',
  {
    bg: string;
    fog: [string, number, number];
    hemi: [string, string, number];
    key: [string, number];
    rim: [string, number];
    sky: [string, string, string];
  }
> = {
  day: {
    bg: '#9fb0c2',
    fog: ['#9fb0c2', 30, 120],
    hemi: ['#dbe4f0', '#2a2d34', 2.2],
    key: ['#fff6ee', 2.8],
    rim: ['#a9bfee', 1.4],
    sky: ['#5f7896', '#c9d5e2', '#ffffff'],
  },
  dawn: {
    bg: '#8f9db0',
    fog: ['#97a4b6', 28, 110],
    hemi: ['#d0d9e6', '#25272e', 2.1],
    key: ['#ffe9de', 2.6],
    rim: ['#a9bfee', 1.5],
    sky: ['#4f617d', '#c7d0dc', '#ffe1d2'],
  },
  dusk: {
    bg: '#3d475b',
    fog: ['#46506a', 28, 120],
    hemi: ['#c3cfe2', '#1c1f27', 2.6],
    key: ['#ffe2d4', 3.0],
    rim: ['#9ab4f0', 2.0],
    sky: ['#18203a', '#6a7694', '#ff9a7a'],
  },
  night: {
    bg: '#0a0e16',
    fog: ['#0e1420', 24, 95],
    hemi: ['#8496bf', '#0b0c10', 1.55],
    key: ['#b3c3ea', 1.25],
    rim: ['#7a96ff', 1.4],
    sky: ['#03050b', '#121a28', '#a7b8e6'],
  },
  // Inside the library: the cold stone lit by the embers and the page (the key a pale cold, the rim warm off the coals).
  inside: {
    bg: '#060607',
    fog: ['#0a0909', 22, 70],
    hemi: ['#aeb8c8', '#24160e', 2.2],
    key: ['#dfe6f2', 2.1],
    rim: ['#ff6a3a', 1.7],
    sky: ['#000000', '#000000', '#000000'],
  },
};

/** The night sea round the stilts: a slow swell, dark, with the lanterns' streaks. */
const seaMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, dawn: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vXZ = w.xz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time; uniform float dawn; varying vec2 vXZ;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float sw = n(vXZ * 0.35 + vec2(time * 0.07, time * 0.03)) * 0.6 + n(vXZ * 0.9 - vec2(time * 0.11, 0.0)) * 0.4;
        float glint = smoothstep(0.78, 0.9, n(vec2(vXZ.x * 1.4 + time * 0.2, vXZ.y * 3.0 + sw * 2.0)));
        vec3 deep = mix(vec3(0.03, 0.06, 0.10), vec3(0.12, 0.17, 0.24), dawn);
        vec3 col = mix(deep, deep * 1.9 + vec3(0.02, 0.03, 0.04), sw * 0.7);
        col += glint * mix(vec3(0.25, 0.32, 0.45), vec3(0.55, 0.6, 0.68), dawn) * 0.5;
        gl_FragColor = vec4(col, 1.0);
      }`,
  });

/** The flames of a wall of fire: additive, flickering up their height (instanced cones, per-instance phase in color). */
const flameMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 } },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    vertexShader: /* glsl */ `
      uniform float time; varying float vY; varying float vPh;
      void main() {
        vec3 p = position;
        vPh = fract(sin(dot(instanceMatrix[3].xz, vec2(12.9898, 78.233))) * 43758.5453);
        float k = clamp(p.y, 0.0, 1.0);
        p.y *= 1.0 + 0.28 * sin(time * 5.0 + vPh * 20.0);
        p.x += sin(time * 9.0 + vPh * 30.0 + p.y * 6.0) * 0.08 * k;
        p.z += cos(time * 7.0 + vPh * 20.0 + p.y * 5.0) * 0.08 * k;
        vY = k;
        gl_Position = projectionMatrix * viewMatrix * modelMatrix * instanceMatrix * vec4(p, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform float time; varying float vY; varying float vPh;
      void main() {
        float f = 0.75 + 0.25 * sin(time * 13.0 + vPh * 40.0);
        vec3 core = vec3(1.0, 0.62, 0.34);
        vec3 edge = vec3(0.95, 0.22, 0.10);
        vec3 col = mix(core, edge, smoothstep(0.0, 0.75, vY));
        float a = (1.0 - vY) * (1.0 - vY * 0.5) * 0.62 * f;
        gl_FragColor = vec4(col * a, a);
      }`,
  });

/** How many firelight pools the floor shader takes (the strongest ember lights). */
const EMBER_PTS = 16;

/** The library's floor detail, multiplied over the board: flagstones and charred planks, their joints, soot stains and
 *  scorch rings (white where there is no floor: the mask of the open cells). One draped plane, no light of its own. */
const floorDetailMat = (mask: THREE.Texture, box: THREE.Vector4): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { mask: { value: mask }, box: { value: box } },
    transparent: true,
    depthWrite: false,
    blending: THREE.MultiplyBlending,
    premultipliedAlpha: true,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vXZ = w.xz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform sampler2D mask; uniform vec4 box; varying vec2 vXZ;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float m = texture2D(mask, (vXZ - box.xy) / box.zw).r;
        // Flagstones: courses of uneven slabs, each its own tone, cracked here and there.
        vec2 p = vXZ / vec2(1.05, 0.8);
        float row = floor(p.y);
        p.x += h(vec2(row, 3.0)) * 0.9;
        vec2 c = floor(p); vec2 f = fract(p);
        float e = min(min(f.x, 1.0 - f.x) * 1.05, min(f.y, 1.0 - f.y) * 0.8);
        float joint = smoothstep(0.008, 0.04, e);
        float tone = 0.62 + 0.38 * h(c);
        vec3 col = vec3(tone) * vec3(0.97, 0.98, 1.0);
        float crack = smoothstep(0.014, 0.0, abs(n(vXZ * 3.1 + c * 7.0) - 0.5)) * step(0.78, h(c + 9.0));
        // Charred planks where the floor was boarded: long boards, burnt darker toward their ends.
        float pr = smoothstep(0.48, 0.56, n(vXZ * 0.16 + 4.3));
        {
          float bw = 0.34;
          float pk = floor(vXZ.x / bw);
          float fx = fract(vXZ.x / bw);
          float sh = h(vec2(pk, 1.0)) * 3.0;
          float seg = floor(vXZ.y / 1.9 + sh);
          float fy = fract(vXZ.y / 1.9 + sh);
          float pj = smoothstep(0.0, 0.08, min(fx, 1.0 - fx)) * smoothstep(0.0, 0.025, min(fy, 1.0 - fy));
          float grain = 0.85 + 0.15 * sin(vXZ.y * 26.0 + n(vXZ * vec2(3.0, 0.6)) * 6.0);
          vec3 pc = vec3((0.42 + 0.3 * h(vec2(pk, seg))) * grain) * vec3(0.86, 0.8, 0.77);
          col = mix(col, pc, pr);
          joint = mix(joint, pj, pr);
          crack *= 1.0 - pr;
        }
        col *= mix(0.42, 1.0, joint);
        col *= 1.0 - crack * 0.4;
        // Soot: broad stains and scorch rings where something burnt out.
        float soot = smoothstep(0.3, 0.75, n(vXZ * 0.42 + 1.7)) * 0.6 + smoothstep(0.45, 0.9, n(vXZ * 1.3 - 5.0)) * 0.3;
        vec2 sc = floor(vXZ / 3.2); vec2 sf = fract(vXZ / 3.2) - 0.5 + (vec2(h(sc), h(sc + 2.0)) - 0.5) * 0.4;
        float ring = step(0.6, h(sc + 5.0)) * smoothstep(0.45, 0.08, length(sf)) * 0.6;
        col *= 1.0 - min(0.82, soot + ring);
        // Ash drifts: paler, soft (left as they are: the base is the ash's colour).
        float ash = smoothstep(0.6, 0.85, n(vXZ * 0.9 + 11.0));
        col = mix(col, vec3(0.92, 0.93, 0.95), ash * 0.4);
        gl_FragColor = vec4(mix(vec3(1.0), col, m), 1.0);
      }`,
  });

/** The embers in the floor, added over it: veins of brasa along the joints and cracks, live coals, and the warm pools
 *  the burning shelves throw on the floor (`pts`: x, z, reach, strength). Out (`quench`) once the cabinet opens. */
const floorEmberMat = (mask: THREE.Texture, box: THREE.Vector4, pts: THREE.Vector4[]): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { mask: { value: mask }, box: { value: box }, time: { value: 0 }, quench: { value: 0 }, pts: { value: pts } },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vXZ = w.xz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform sampler2D mask; uniform vec4 box; uniform float time; uniform float quench; uniform vec4 pts[${EMBER_PTS}];
      varying vec2 vXZ;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float m = texture2D(mask, (vXZ - box.xy) / box.zw).r;
        if (m < 0.02 || quench > 0.5) discard;
        // Veins: thin glowing lines where the noise crosses its middle, only in burnt patches, breathing.
        float scorch = smoothstep(0.66, 0.82, n(vXZ * 0.35 + 7.0));
        float v = smoothstep(0.022, 0.0, abs(n(vXZ * 1.6) - 0.5)) * scorch;
        float beat = 0.65 + 0.35 * sin(time * 2.2 + n(vXZ * 0.5) * 12.0);
        // Live coals: a few small cells lit.
        vec2 cc = floor(vXZ / 0.22); vec2 cf = fract(vXZ / 0.22) - 0.5;
        float coal = step(0.975, h(cc)) * smoothstep(0.35, 0.05, length(cf)) * (0.6 + 0.4 * sin(time * 4.0 + h(cc + 1.0) * 30.0));
        // The firelight's pools on the floor.
        float pool = 0.0;
        for (int i = 0; i < ${EMBER_PTS}; i++) {
          vec4 q = pts[i];
          if (q.w <= 0.0) continue;
          vec2 d = vXZ - q.xy;
          pool += q.w * exp(-dot(d, d) / (q.z * q.z));
        }
        float fl = 0.9 + 0.1 * sin(time * 7.0 + vXZ.x);
        vec3 col = vec3(1.0, 0.36, 0.08) * v * beat * 0.8 + vec3(1.0, 0.5, 0.15) * coal * 1.2 + vec3(0.46, 0.22, 0.04) * min(pool, 1.1) * fl;
        gl_FragColor = vec4(col * m, 1.0);
      }`,
  });

interface SealView {
  g: THREE.Group;
  shell: THREE.Group;
  open: THREE.Group;
  at: THREE.Vector3;
  kind: 0 | 1 | 2;
}

export class BiblioLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(260);
  private dome: SkyDome | null = null;
  private terrain: TerrainBuild | null = null;
  private inside: boolean;
  private now = 0;
  private lights: {
    p: THREE.Vector3;
    c: THREE.Color;
    size: number;
    a: number;
    seed: number;
    flick: number;
  }[] = [];
  /** Lit while the room burns; out once the cabinet opens (the fire room, and its close). */
  private embersOut = false;
  private sea: THREE.ShaderMaterial | null = null;
  private seaY = 0;
  /** The library's open floor (its skin of slabs, planks, soot and embers is drawn over it) and the embers' shader. */
  private floorCells: { q: number; r: number }[] = [];
  private emberMat: THREE.ShaderMaterial | null = null;
  // The cellars.
  private Sl: SootLayout | null = null;
  private piles: { m: THREE.Group; hall: number; k: number; flat: number }[] = [];
  private beams: { m: THREE.Group; y: number; want: number }[] = [];
  private shelvesBurn: THREE.Vector3[] = [];
  // The locks and the cabinet.
  private Ll: LecternLayout | null = null;
  private Cl: CabinetLayout | null = null;
  private seals: SealView[] = [];
  private items: { m: THREE.Group; at: THREE.Vector3; kind: 0 | 1 | 2 }[] = [];
  private doors: THREE.Group[] = [];
  private flames: THREE.InstancedMesh | null = null;
  private floorRed: THREE.InstancedMesh | null = null;
  private flameMat: THREE.ShaderMaterial | null = null;
  private redMat: THREE.MeshBasicMaterial | null = null;
  private spokeAt: THREE.Vector3[][] = [];
  private runes: { p: THREE.Vector3; t0: number; t1: number }[] = [];
  private writing = new Map<number, number>();
  private flash = 0;
  private flameKey = -2;
  private lastState: BattleState | null = null;
  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `biblio-${place}`;
    this.inside = place !== 'stilts' && place !== 'pier' && place !== 'labYard';
    const kit = new Kit();
    if (place === 'sootCellars') this.buildCellars(kit, s);
    else if (place === 'lecterns') this.buildLecterns(kit, s);
    else if (place === 'fireRoom') this.buildFireRoom(kit, s, false);
    else if (place === 'fireOut') this.buildFireRoom(kit, s, true);
    else if (place === 'stilts' || place === 'pier') this.buildStilts(kit, place === 'pier');
    else if (place === 'labYard') this.buildYard(kit);
    else if (place === 'catalog') this.buildCatalogRoom(kit);
    else this.buildCollapse(kit);
    const mat = this.track(
      new THREE.MeshStandardMaterial({
        vertexColors: true,
        roughness: 0.88,
        metalness: 0.05,
        flatShading: true,
      }),
    );
    const dress = kit.build(mat, 'biblio-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    if (this.floorCells.length) this.floorSkin(this.floorCells);
    this.group.add(this.glow.mesh);
  }

  /**
   * The burnt floor over the board's open cells: one plane draped on the ground, multiplied (slabs, charred planks,
   * soot, ash), and the same plane again added (veins of embers, coals, the shelves' firelight pooled on the floor).
   * The open cells are a small mask texture, so the walls and what lies beyond them are left alone. Two draw calls.
   */
  private floorSkin(cells: { q: number; r: number }[]): void {
    let x0 = Infinity;
    let z0 = Infinity;
    let x1 = -Infinity;
    let z1 = -Infinity;
    const pts = cells.map((c) => this.hex(c.q, c.r));
    for (const p of pts) {
      x0 = Math.min(x0, p.x - S);
      z0 = Math.min(z0, p.z - S);
      x1 = Math.max(x1, p.x + S);
      z1 = Math.max(z1, p.z + S);
    }
    const w = x1 - x0;
    const d = z1 - z0;
    const N = 256;
    const cv = document.createElement('canvas');
    cv.width = cv.height = N;
    const g = cv.getContext('2d');
    if (!g) return;
    g.fillStyle = '#000';
    g.fillRect(0, 0, N, N);
    g.filter = 'blur(2px)';
    g.fillStyle = '#fff';
    for (const p of pts) {
      g.beginPath();
      g.ellipse(((p.x - x0) / w) * N, ((p.z - z0) / d) * N, ((0.62 * S) / w) * N, ((0.62 * S) / d) * N, 0, 0, Math.PI * 2);
      g.fill();
    }
    const mask = this.trackTex(new THREE.CanvasTexture(cv));
    mask.flipY = false;
    const box = new THREE.Vector4(x0, z0, w, d);
    const geo = this.track(new THREE.PlaneGeometry(w, d, 72, 72));
    geo.rotateX(-Math.PI / 2);
    geo.translate(x0 + w / 2, 0, z0 + d / 2);
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) pos.setY(i, this.ctx.ground(pos.getX(i), pos.getZ(i)) + 0.012);
    pos.needsUpdate = true;
    geo.computeBoundingSphere();
    const detail = new THREE.Mesh(geo, this.track(floorDetailMat(mask, box)));
    detail.renderOrder = -2;
    detail.name = 'biblio-floor';
    this.group.add(detail);
    // The firelight pools: the strongest warm lights near the floor (the burning shelves, the coals).
    const warm = this.lights
      .filter((l) => l.c.r > l.c.b + 0.3 && l.p.y < 2.4)
      .sort((a, b) => b.size * b.a - a.size * a.a)
      .slice(0, EMBER_PTS)
      .map((l) => new THREE.Vector4(l.p.x, l.p.z, 0.9 + l.size * 0.85, Math.min(0.75, 0.25 + l.size * l.a * 0.28)));
    while (warm.length < EMBER_PTS) warm.push(new THREE.Vector4(0, 0, 1, 0));
    this.emberMat = this.track(floorEmberMat(mask, box, warm));
    (this.emberMat.uniforms.quench as THREE.IUniform<number>).value = this.embersOut ? 1 : 0;
    const embers = new THREE.Mesh(geo, this.emberMat);
    embers.renderOrder = -1;
    embers.name = 'biblio-embers';
    this.group.add(embers);
  }

  private texs: THREE.Texture[] = [];
  private trackTex<T extends THREE.Texture>(t: T): T {
    this.texs.push(t);
    return t;
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private at(x: number, y: number, h = 0): THREE.Vector3 {
    return this.ctx.at(x, y, h);
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number, flick = 0): void {
    this.lights.push({
      p: p.clone(),
      c: new THREE.Color(color),
      size,
      a,
      seed: this.lights.length * 1.37,
      flick,
    });
  }

  private std(color: string, o: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial {
    return this.track(
      new THREE.MeshStandardMaterial({
        color,
        roughness: 0.8,
        metalness: 0.05,
        flatShading: true,
        ...o,
      }),
    );
  }

  private mesh(geo: THREE.BufferGeometry, mat: THREE.Material, parent: THREE.Object3D): THREE.Mesh {
    this.track(geo);
    const m = new THREE.Mesh(geo, mat);
    m.castShadow = true;
    parent.add(m);
    return m;
  }

  /** Burnt walls round the open cells: blocks of soot-black stone, a shelf of books on many of their faces. */
  private walls(kit: Kit, rocks: { q: number; r: number }[], open: Set<string>, tall = 3.2, lowNear = true): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const dirs = [
      [1, 0],
      [1, -1],
      [0, -1],
      [-1, 0],
      [-1, 1],
      [0, 1],
    ] as const;
    for (const h of rocks) {
      const by = dirs.filter(([dq, dr]) => open.has(`${h.q + dq},${h.r + dr}`));
      if (!by.length) continue;
      const p = this.hex(h.q, h.r);
      const k = Math.sin(h.q * 12.9 + h.r * 78.2) * 0.5 + 0.5;
      let fx = 0;
      let fz = 0;
      for (const [dq, dr] of by) {
        const c = hexToWorld(dq, dr);
        fx += c.x;
        fz += c.y;
      }
      const yaw = Math.atan2(fx, fz);
      // (Seen from the camera's side, low: full height they were a black band over the bottom of the screen.)
      // (Any open floor north of it: it stands between the camera and a hall, so low.)
      const near = lowNear && (fz < -0.3 * Math.hypot(fx, fz) || by.some(([dq, dr]) => hexToWorld(dq, dr).y < 0));
      const t = (near ? 0.8 : tall) + k * 0.3;
      kit.add(box, SOOT[Math.floor(k * 3.99)] as string, place(p.x, p.y + t * 0.5, p.z, yaw, new THREE.Vector3(1.2 * S, t, 1.2 * S)));
      kit.add(box, '#1d1e21', place(p.x, p.y + t + 0.05, p.z, yaw, new THREE.Vector3(1.24 * S, 0.1, 1.24 * S)));
      if (near) continue;
      this.bookcase(kit, box, p, yaw, h.q, h.r, t);
    }
    box.dispose();
  }

  /**
   * A tall bookcase on a wall's face (built in its own frame: x along the face, y up, z out of the wall): dark oak gone
   * black, five shelves, books standing and leaning with gaps where they burnt; some cases broken (a shelf snapped and
   * hanging, a post burnt short and the crown gone), some leaning off the wall; books fallen at its foot, one lying
   * open; now and then a beam fallen from above against it. Burnt cases glow low with embers.
   */
  private bookcase(kit: Kit, box: THREE.BoxGeometry, p: THREE.Vector3, yaw: number, q: number, r: number, wallT: number): void {
    const hs = (a: number): number => {
      const v = Math.sin(q * 12.989 + r * 78.233 + a * 37.719) * 43758.5453;
      return v - Math.floor(v);
    };
    const fx = Math.sin(yaw);
    const fz = Math.cos(yaw);
    const lean = hs(1) > 0.78 ? 0.06 + hs(2) * 0.05 : 0;
    const M = place(p.x + fx * 0.6 * S, p.y, p.z + fz * 0.6 * S, yaw, 1, lean);
    const L = (x: number, y: number, z: number, sx: number, sy: number, sz: number, tx = 0, tz = 0): THREE.Matrix4 => place(x, y, z, 0, new THREE.Vector3(sx, sy, sz), tx, tz);
    const W = 1.06 * S;
    const H = Math.max(2.9, wallT + 0.3);
    const burnt = hs(3) < 0.4;
    const broken = hs(4) > 0.55;
    const wood = burnt ? (CHAR[2] as string) : (CHAR[0] as string);
    // Back, posts, plinth; a broken case has its right post burnt short and no crown.
    kit.add(box, CHAR[2] as string, M, L(0, H / 2, 0.05, W, H, 0.1));
    kit.add(box, wood, M, L(-W / 2 + 0.05, H / 2, 0.2, 0.1, H, 0.36));
    const rp = broken ? H * (0.5 + hs(5) * 0.25) : H;
    kit.add(box, wood, M, L(W / 2 - 0.05, rp / 2, 0.2, 0.1, rp, 0.36));
    kit.add(box, wood, M, L(0, 0.08, 0.22, W, 0.16, 0.38));
    if (broken) {
      // The crown's stump: a jagged end hanging off the left post.
      kit.add(box, CHAR[1] as string, M, L(-W / 4, H - 0.06, 0.2, W / 2, 0.12, 0.38, 0, -0.12));
    } else kit.add(box, wood, M, L(0, H + 0.06, 0.22, W + 0.12, 0.12, 0.42));
    const shelves = 5;
    for (let sh = 0; sh < shelves; sh++) {
      const y = 0.22 + sh * ((H - 0.35) / shelves);
      if (broken && y > rp) continue;
      const snapped = hs(10 + sh) > 0.82;
      // A shelf snapped in two: its halves hang down to the middle, the books slid off into a heap below.
      if (snapped) {
        kit.add(box, CHAR[1] as string, M, L(-W / 4 - 0.02, y - 0.12, 0.2, W / 2, 0.05, 0.32, 0, -0.38));
        kit.add(box, CHAR[1] as string, M, L(W / 4 + 0.02, y - 0.1, 0.2, W / 2, 0.05, 0.32, 0, 0.32));
        continue;
      }
      kit.add(box, CHAR[1] as string, M, L(0, y - 0.03, 0.2, W - 0.12, 0.05, 0.32));
      let x = -W / 2 + 0.16;
      let i = 0;
      while (x < W / 2 - 0.16) {
        const k = hs(20 + sh * 13 + i++);
        const bw = 0.08 + k * 0.07;
        // Gaps where books burnt away (more in the burnt cases).
        if (k < (burnt ? 0.42 : 0.16)) {
          x += bw + 0.04;
          continue;
        }
        const tall = 0.3 + hs(60 + sh * 7 + i) * 0.16;
        const tilt = k > 0.9 ? 0.4 : k > 0.82 ? -0.3 : 0;
        const col = (burnt && k < 0.7 ? CHAR[2] : BOOKS[Math.floor(hs(90 + i + sh) * 5.99)]) as string;
        kit.add(box, col, M, L(x + bw / 2, y + tall / 2 + (tilt ? -0.03 : 0), 0.22, bw, tall, 0.22, 0, tilt));
        x += bw + 0.01 + (tilt ? 0.08 : 0);
      }
    }
    // Books fallen at its foot: a few lying, one open (its pages a cold grey, scorched at the edge).
    const nf = 2 + Math.floor(hs(30) * 3);
    for (let i = 0; i < nf; i++) {
      const a = hs(31 + i) * 6.28;
      kit.add(box, BOOKS[Math.floor(hs(40 + i) * 5.99)] as string, M, place(-W * 0.4 + hs(50 + i) * W * 0.8, 0.04 + (i % 2) * 0.05, 0.5 + hs(55 + i) * 0.35, a, new THREE.Vector3(0.24, 0.06, 0.32)));
    }
    if (hs(6) > 0.45) {
      const ox = -W * 0.3 + hs(7) * W * 0.6;
      kit.add(box, '#2a2220', M, L(ox, 0.02, 0.78, 0.52, 0.03, 0.36));
      kit.add(box, '#a7abb3', M, L(ox - 0.125, 0.06, 0.78, 0.24, 0.02, 0.32, 0, -0.14));
      kit.add(box, '#9ca0a8', M, L(ox + 0.125, 0.06, 0.78, 0.24, 0.02, 0.32, 0, 0.14));
      kit.add(box, '#3a2a24', M, L(ox + 0.2, 0.07, 0.78, 0.06, 0.02, 0.3, 0, 0.14));
    }
    // A beam fallen from above, leaning on the case down to the floor.
    if (hs(9) > 0.8 && lean === 0) {
      const top = H - 0.2;
      const out = 1.05;
      const len = Math.hypot(top, out);
      const ang = Math.atan2(top, out);
      kit.add(box, '#231b16', M, L(-W * 0.3 + hs(11) * W * 0.6, top / 2, 0.35 + out / 2, 0.2, 0.22, len, ang, 0.15));
      this.light(new THREE.Vector3(p.x + fx * (0.6 * S + 0.9), p.y + 0.25, p.z + fz * (0.6 * S + 0.9)), EMBER, 0.8, 0.5, 1);
    }
    if (burnt) this.light(new THREE.Vector3(p.x + fx * (0.6 * S + 0.3), p.y + 0.5 + hs(12) * 0.6, p.z + fz * (0.6 * S + 0.3)), EMBER, 1.1, 0.6, 1);
  }

  /** A heap of fallen books and ash on the floor (some of its leaves still glowing at the edges). */
  private heap(kit: Kit, x: number, z: number, y: number, k: number, ember = false): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const ash = new THREE.DodecahedronGeometry(0.5, 0);
    kit.add(ash, '#5a5856', place(x, y + 0.04, z, k * 3, new THREE.Vector3(0.8, 0.14, 0.7)));
    for (let i = 0; i < 5; i++) {
      const a = k * 7 + i * 1.3;
      kit.add(
        box,
        (i % 2 ? CHAR[2] : BOOKS[(i + Math.floor(k * 10)) % BOOKS.length]) as string,
        place(x + Math.cos(a) * 0.25, y + 0.08 + i * 0.03, z + Math.sin(a) * 0.25, a, new THREE.Vector3(0.3, 0.06, 0.22), 0.2, 0.1),
      );
    }
    box.dispose();
    ash.dispose();
    if (ember) this.light(new THREE.Vector3(x, y + 0.15, z), EMBER, 0.9, 0.5, 1);
  }

  /** A pile of ash and bones where a risen sleeps (its own group: flattened once it rises). */
  private pile(x: number, y: number): THREE.Group {
    const g = new THREE.Group();
    const p = this.at(x, y);
    g.position.copy(p);
    const ash = this.std('#5c5a57', { roughness: 1 });
    const bone = this.std('#c9c3b6', { roughness: 0.85 });
    const m = this.mesh(new THREE.SphereGeometry(0.5, 9, 5, 0, Math.PI * 2, 0, Math.PI / 2), ash, g);
    m.scale.set(1, 0.42, 0.9);
    for (let i = 0; i < 4; i++) {
      const b = this.mesh(new THREE.CylinderGeometry(0.03, 0.035, 0.38, 5), bone, g);
      b.position.set(Math.cos(i * 1.7) * 0.25, 0.14, Math.sin(i * 1.7) * 0.22);
      b.rotation.set(Math.PI / 2, i * 0.9, 0.3);
    }
    const skull = this.mesh(new THREE.SphereGeometry(0.11, 8, 6), bone, g);
    skull.position.set(0.12, 0.2, -0.05);
    this.group.add(g);
    return g;
  }

  /** Charred beams fallen across a stair (its own group, dropped in or lifted away). */
  private beamPile(x: number, y: number): THREE.Group {
    const g = new THREE.Group();
    g.position.copy(this.at(x, y));
    const wood = this.std('#2a221c', { roughness: 0.95 });
    const ember = this.track(
      new THREE.MeshStandardMaterial({
        color: '#3a1a10',
        emissive: '#ff4f2a',
        emissiveIntensity: 0.9,
        roughness: 0.9,
      }),
    );
    for (let i = 0; i < 3; i++) {
      const b = this.mesh(new THREE.BoxGeometry(2.6, 0.24, 0.26), i === 1 ? ember : wood, g);
      b.position.set(0, 0.18 + i * 0.2, (i - 1) * 0.3);
      b.rotation.set(0, (i - 1) * 0.35, (i - 1) * 0.12);
    }
    this.group.add(g);
    return g;
  }

  /* ---------- the cellars ---------- */

  private buildCellars(kit: Kit, s: BattleState): void {
    const L = sootLayout(s.seed);
    this.Sl = L;
    this.floorCells = L.floor;
    const open = new Set(L.floor.map((h) => `${h.q},${h.r}`));
    this.walls(kit, L.rocks, open);
    const box = new THREE.BoxGeometry(1, 1, 1);
    L.halls.forEach((h, i) => {
      // The fallen shelf across the hall: a long case on its side, books spilled.
      for (const c of h.fallen) {
        const p = this.hex(c.q, c.r);
        kit.add(box, CHAR[0] as string, place(p.x, p.y + 0.3, p.z, 0.2, new THREE.Vector3(1.25 * S, 0.6, 0.6)));
        this.heap(kit, p.x + 0.3, p.z + 0.55, p.y, c.q * 0.37 + i, true);
      }
      // Heaps of fallen books here and there along the walls.
      for (let k = 0; k < 4; k++) {
        const c = h.cells[(k * 7 + i * 3) % h.cells.length];
        if (!c) continue;
        const p = this.hex(c.q, c.r);
        if (h.piles.some((x) => Math.abs(x.x - hexToWorld(c.q, c.r).x) + Math.abs(x.y - hexToWorld(c.q, c.r).y) < 1300)) continue;
        this.heap(kit, p.x, p.z, p.y, k * 0.31 + i * 0.7, k === 0);
      }
      // Charred beams fallen from the ceiling, crossed on the floor (low: walked over), one still glowing.
      for (let k = 0; k < 2; k++) {
        const c = h.cells[(k * 11 + i * 5 + 3) % h.cells.length];
        if (!c) continue;
        const w0 = hexToWorld(c.q, c.r);
        if (h.piles.some((x) => Math.abs(x.x - w0.x) + Math.abs(x.y - w0.y) < 1500)) continue;
        const p = this.hex(c.q, c.r);
        const a = (c.q * 1.7 + c.r * 0.9 + k) % 3.14;
        kit.add(box, '#231b16', place(p.x, p.y + 0.1, p.z, a, new THREE.Vector3(0.22, 0.2, 2.3), 0, 0.08));
        kit.add(box, CHAR[1] as string, place(p.x + 0.1, p.y + 0.26, p.z - 0.05, a + 1.1, new THREE.Vector3(0.2, 0.18, 1.9), 0.06, -0.1));
        if (k === 0) {
          kit.add(box, '#4a1e10', place(p.x, p.y + 0.21, p.z, a, new THREE.Vector3(0.12, 0.03, 0.9)));
          this.light(p.clone().setY(p.y + 0.3), EMBER, 0.9, 0.55, 1);
        }
      }
      // The burning shelves where the pages come off.
      for (const sh of h.shelves) {
        const p = this.at(sh.x, sh.y, 0.9);
        this.shelvesBurn.push(p);
        this.light(p, EMBER, 2.2, 0.65, 1);
      }
      h.piles.forEach((pl, k) => this.piles.push({ m: this.pile(pl.x, pl.y), hall: i, k, flat: 1 }));
      if (h.stair.length) {
        const a = hexToWorld((h.stair[0] as { q: number }).q, (h.stair[0] as { r: number }).r);
        const b = hexToWorld((h.stair[1] as { q: number }).q, (h.stair[1] as { r: number }).r);
        this.beams.push({
          m: this.beamPile((a.x + b.x) / 2, (a.y + b.y) / 2),
          y: 3,
          want: 3,
        });
      }
    });
    // The collapse at the way in: beams and stones, light falling from above (Isra's lantern).
    const st = this.at(L.start.x, L.start.y - 1400);
    for (let i = 0; i < 5; i++) kit.add(box, CHAR[i % 3] as string, place(st.x + (i - 2) * 0.4, st.y + 0.3 + (i % 2) * 0.3, st.z, 0.4 * (i - 2), new THREE.Vector3(0.3, 0.3, 2.4), 0.3, 0.2));
    this.light(new THREE.Vector3(st.x, st.y + 2.2, st.z), COLD, 3.4, 0.55, 0.2);
    this.catalogWall(kit, L.catalog.x, L.catalog.y + 1000, 0, 1);
    box.dispose();
  }

  /** The catalog's wall of bronze drawers, facing −z (into the hall); one drawer clean and drawn out. */
  private catalogWall(kit: Kit, x: number, y: number, yaw: number, f = -1): void {
    const p = this.at(x, y);
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, '#2a2b2f', place(p.x, p.y + 1.3, p.z - f * 0.15, yaw, new THREE.Vector3(3.6, 2.6, 0.4)));
    for (let r = 0; r < 6; r++)
      for (let c = 0; c < 9; c++) {
        const clean = r === 3 && c === 5;
        const k = Math.sin(r * 4.1 + c * 2.7) * 0.5 + 0.5;
        const col = clean ? '#a08a72' : k > 0.5 ? '#3b3430' : '#2e2925';
        kit.add(box, col, place(p.x - 1.6 + c * 0.4, p.y + 0.35 + r * 0.38, p.z + f * (0.07 + (clean ? 0.22 : 0)), yaw, new THREE.Vector3(0.34, 0.3, clean ? 0.5 : 0.08)));
        kit.add(box, clean ? '#d8dde4' : BRONZE, place(p.x - 1.6 + c * 0.4, p.y + 0.35 + r * 0.38, p.z + f * (0.12 + (clean ? 0.45 : 0)), yaw, new THREE.Vector3(0.08, 0.03, 0.03)));
      }
    box.dispose();
    this.light(new THREE.Vector3(p.x - 1.6 + 5 * 0.4, p.y + 0.35 + 3 * 0.38, p.z + f * 0.7), COLD, 1.4, 0.5, 0.3);
  }

  /* ---------- the locks ---------- */

  /** An iron lectern with its sealed book (its own group for the seal and the open book). */
  private lectern(kit: Kit, x: number, y: number, kind: 0 | 1 | 2, yaw: number, high = 1.05): SealView {
    const p = this.at(x, y);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
    kit.add(cyl, IRON, place(p.x, p.y + 0.05, p.z, 0, new THREE.Vector3(0.9, 0.1, 0.9)));
    kit.add(cyl, '#3a3f46', place(p.x, p.y + high * 0.5, p.z, 0, new THREE.Vector3(0.14, high, 0.14)));
    kit.add(box, IRON, place(p.x, p.y + high, p.z, yaw, new THREE.Vector3(0.9, 0.06, 0.62), -0.35));
    box.dispose();
    cyl.dispose();
    return this.sealView(p.clone().setY(p.y + high + 0.12), kind, yaw);
  }

  /** A sealed book's seal: frost (a shell of blue ice), crust (a green lumpy crust), ink (black, red threads). */
  private sealView(at: THREE.Vector3, kind: 0 | 1 | 2, yaw: number): SealView {
    const g = new THREE.Group();
    g.position.copy(at);
    g.rotation.y = yaw;
    const shell = new THREE.Group();
    const open = new THREE.Group();
    g.add(shell, open);
    const cover = this.std('#3a2622', { roughness: 0.7 });
    this.mesh(new THREE.BoxGeometry(0.64, 0.12, 0.46), cover, shell).rotation.x = -0.35;
    if (kind === 0) {
      const ice = this.track(
        new THREE.MeshStandardMaterial({
          color: '#cfe6ff',
          emissive: '#3a78c8',
          emissiveIntensity: 0.55,
          roughness: 0.2,
          metalness: 0.1,
          transparent: true,
          opacity: 0.9,
          flatShading: true,
        }),
      );
      for (let i = 0; i < 6; i++) {
        const c = this.mesh(new THREE.ConeGeometry(0.07, 0.34, 5), ice, shell);
        c.position.set(-0.22 + i * 0.09, 0.14, Math.sin(i * 2.1) * 0.12);
        c.rotation.set(Math.sin(i) * 0.4, 0, Math.cos(i * 1.3) * 0.4);
      }
    } else if (kind === 1) {
      const crust = this.track(
        new THREE.MeshStandardMaterial({
          color: '#5f8a3a',
          emissive: '#2f7a1c',
          emissiveIntensity: 0.5,
          roughness: 0.95,
          flatShading: true,
        }),
      );
      for (let i = 0; i < 7; i++) {
        const c = this.mesh(new THREE.DodecahedronGeometry(0.11, 0), crust, shell);
        c.position.set(-0.24 + i * 0.08, 0.1 + (i % 2) * 0.03, Math.cos(i * 1.7) * 0.12);
        c.scale.set(1, 0.6, 1);
      }
    } else {
      const ink = this.track(
        new THREE.MeshStandardMaterial({
          color: '#0b0a0c',
          roughness: 0.15,
          metalness: 0.4,
        }),
      );
      const thread = this.track(
        new THREE.MeshStandardMaterial({
          color: '#400c08',
          emissive: '#ff3a2a',
          emissiveIntensity: 1.6,
        }),
      );
      this.mesh(new THREE.BoxGeometry(0.62, 0.14, 0.44), ink, shell).rotation.x = -0.35;
      for (let i = 0; i < 5; i++) {
        const t = this.mesh(new THREE.TorusGeometry(0.24 + i * 0.02, 0.008, 3, 18), thread, shell);
        t.rotation.set(Math.PI / 2 + Math.sin(i) * 0.6, i * 0.7, 0);
        t.position.y = 0.06;
      }
    }
    // Open: the cover thrown back and the leaves standing up.
    const leaf = this.std('#d9d4c8', { roughness: 1, side: THREE.DoubleSide });
    this.mesh(new THREE.BoxGeometry(0.64, 0.04, 0.46), cover, open).position.set(0, -0.02, 0);
    for (let i = 0; i < 4; i++) {
      const l = this.mesh(new THREE.PlaneGeometry(0.3, 0.4), leaf, open);
      l.position.set(-0.1 + i * 0.06, 0.18, 0);
      l.rotation.set(0, 0, 0.4 - i * 0.25);
    }
    open.visible = false;
    this.group.add(g);
    const v = { g, shell, open, at: at.clone(), kind };
    this.seals.push(v);
    return v;
  }

  /** A thing by a seal: a stone bowl of frost, a glass flask of acid, an iron brazier (its own group). */
  private item(x: number, y: number, kind: 0 | 1 | 2): void {
    const g = new THREE.Group();
    const p = this.at(x, y);
    g.position.copy(p);
    const stand = this.std('#3c3f45');
    this.mesh(new THREE.CylinderGeometry(0.16, 0.2, 0.42, 7), stand, g).position.y = 0.21;
    if (kind === 0) {
      this.mesh(new THREE.CylinderGeometry(0.26, 0.16, 0.16, 9), this.std('#7f858c'), g).position.y = 0.5;
      const ice = this.track(
        new THREE.MeshStandardMaterial({
          color: '#d8ecff',
          emissive: '#4a8ad8',
          emissiveIntensity: 0.7,
          roughness: 0.25,
          flatShading: true,
        }),
      );
      for (let i = 0; i < 4; i++) {
        const c = this.mesh(new THREE.ConeGeometry(0.06, 0.24, 5), ice, g);
        c.position.set(Math.cos(i * 1.6) * 0.1, 0.66, Math.sin(i * 1.6) * 0.1);
      }
      this.light(p.clone().setY(p.y + 0.7), FROSTC, 0.9, 0.45, 0.2);
    } else if (kind === 1) {
      const glass = this.track(
        new THREE.MeshStandardMaterial({
          color: '#7fdc7a',
          emissive: '#2a9a2a',
          emissiveIntensity: 0.7,
          roughness: 0.15,
          transparent: true,
          opacity: 0.85,
        }),
      );
      this.mesh(new THREE.SphereGeometry(0.17, 10, 8), glass, g).position.y = 0.6;
      this.mesh(new THREE.CylinderGeometry(0.05, 0.06, 0.14, 6), glass, g).position.y = 0.8;
      this.mesh(new THREE.CylinderGeometry(0.055, 0.05, 0.06, 6), this.std('#5a4636'), g).position.y = 0.89;
      this.light(p.clone().setY(p.y + 0.6), ACIDC, 0.8, 0.4, 0.2);
    } else {
      const iron = this.std(IRON);
      this.mesh(new THREE.CylinderGeometry(0.28, 0.2, 0.14, 9), iron, g).position.y = 0.5;
      const coal = this.track(
        new THREE.MeshStandardMaterial({
          color: '#3a160c',
          emissive: '#ff5a24',
          emissiveIntensity: 1.4,
          flatShading: true,
        }),
      );
      for (let i = 0; i < 5; i++) this.mesh(new THREE.DodecahedronGeometry(0.07, 0), coal, g).position.set(Math.cos(i * 1.3) * 0.12, 0.6, Math.sin(i * 1.3) * 0.12);
      this.light(p.clone().setY(p.y + 0.8), FIREC, 1.3, 0.6, 1);
    }
    this.group.add(g);
    this.items.push({ m: g, at: p.clone(), kind });
  }

  private buildLecterns(kit: Kit, s: BattleState): void {
    const L = lecternLayout(s.seed);
    this.Ll = L;
    this.floorCells = L.floor;
    const open = new Set(L.floor.map((h) => `${h.q},${h.r}`));
    // (The lecterns' own cells are rock in the sim: no wall on them.)
    const lect = new Set(L.lecterns.map((l) => `${l.hex.q},${l.hex.r}`));
    this.walls(
      kit,
      L.rocks.filter((h) => !lect.has(`${h.q},${h.r}`)),
      open,
      3.4,
    );
    for (const l of L.lecterns) this.lectern(kit, l.x, l.y, l.kind, Math.PI);
    for (const it of L.items) this.item(it.x, it.y, it.kind);
    // The door at the top (iron, ajar: where the scribes come in), heaps of books, a reading table overturned.
    const box = new THREE.BoxGeometry(1, 1, 1);
    const d = this.at(L.door.x, L.door.y - 500);
    kit.add(box, '#1e2024', place(d.x, d.y + 1.1, d.z, 0, new THREE.Vector3(1.6, 2.2, 0.3)));
    kit.add(box, IRON, place(d.x - 0.4, d.y + 1.05, d.z + 0.3, 0.5, new THREE.Vector3(0.8, 2.0, 0.08)));
    this.light(new THREE.Vector3(d.x, d.y + 1.0, d.z + 0.2), INKC, 1.6, 0.35, 0.5);
    this.heap(kit, this.at(-4500, 3500).x, this.at(-4500, 3500).z, 0, 0.3, true);
    this.heap(kit, this.at(4600, 3000).x, this.at(4600, 3000).z, 0, 0.8);
    this.heap(kit, this.at(-2000, 5200).x, this.at(-2000, 5200).z, 0, 1.4, true);
    for (const sh of L.shelves) this.light(this.at(sh.x, sh.y, 1.0), EMBER, 1.8, 0.5, 1);
    box.dispose();
  }

  /* ---------- the fire room ---------- */

  private buildFireRoom(kit: Kit, s: BattleState, out: boolean): void {
    const L = cabinetLayout(s.seed);
    this.Cl = L;
    this.embersOut = out;
    this.floorCells = L.floor;
    const open = new Set(L.floor.map((h) => `${h.q},${h.r}`));
    this.walls(
      kit,
      L.rocks.filter((h) => Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(h.q + h.r)) > 1),
      open,
      3.4,
    );
    const box = new THREE.BoxGeometry(1, 1, 1);
    // Ansel's cabinet: a tall iron press, riveted, three faces with a seal on each (the seals are their own groups).
    const c0 = this.hex(0, 0);
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 6);
    kit.add(cyl, '#25282d', place(c0.x, c0.y + 0.08, c0.z, Math.PI / 6, new THREE.Vector3(1.5, 0.16, 1.5)));
    kit.add(cyl, '#33373d', place(c0.x, c0.y + 1.35, c0.z, Math.PI / 6, new THREE.Vector3(1.15, 2.5, 1.15)));
    kit.add(cyl, '#1f2226', place(c0.x, c0.y + 2.68, c0.z, Math.PI / 6, new THREE.Vector3(1.3, 0.16, 1.3)));
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      kit.add(box, '#44494f', place(c0.x + Math.cos(a) * 1.08, c0.y + 1.35, c0.z + Math.sin(a) * 1.08, -a, new THREE.Vector3(0.06, 2.5, 0.1)));
    }
    for (const sh of L.shelves) {
      const sp = this.at(sh.seal.x, sh.seal.y);
      const yaw = Math.atan2(sp.x - c0.x, sp.z - c0.z);
      // The seal's door on the cabinet's face (opened in the close).
      const door = new THREE.Group();
      door.position.set(c0.x + Math.sin(yaw) * 0.98, c0.y + 1.35, c0.z + Math.cos(yaw) * 0.98);
      door.rotation.y = yaw;
      const dm = this.mesh(new THREE.BoxGeometry(0.9, 2.1, 0.06), this.std('#2b2f35', { metalness: 0.4, roughness: 0.5 }), door);
      dm.position.x = out ? 0.45 : 0;
      if (out) door.rotation.y = yaw + 1.2;
      this.group.add(door);
      this.doors.push(door);
      const v = this.sealView(new THREE.Vector3(c0.x + Math.sin(yaw) * 1.12, c0.y + 1.25, c0.z + Math.cos(yaw) * 1.12), sh.seal.kind, yaw);
      v.g.rotation.x = 0;
      if (out) {
        v.shell.visible = false;
        v.open.visible = true;
      }
      // The shelf by the wall: a stone counter blackened, the things on it.
      const m = this.at(sh.x, sh.y);
      const myaw = Math.atan2(m.x - c0.x, m.z - c0.z);
      kit.add(box, '#2a2c30', place(m.x + Math.sin(myaw) * 0.8, m.y + 0.7, m.z + Math.cos(myaw) * 0.8, myaw, new THREE.Vector3(3.4, 1.4, 0.4)));
      if (!out) for (const it of sh.items) this.item(it.x, it.y, it.kind);
    }
    if (out) {
      // The two notes and the clasp, glowing with their ink, at the foot of the cabinet.
      const p = this.at(0, 1500, 0.05);
      kit.add(box, '#d9d4c8', place(p.x - 0.2, p.y + 0.02, p.z, 0.3, new THREE.Vector3(0.3, 0.01, 0.4)));
      kit.add(box, '#d9d4c8', place(p.x + 0.25, p.y + 0.02, p.z + 0.1, -0.4, new THREE.Vector3(0.28, 0.01, 0.36)));
      kit.add(box, BRONZE, place(p.x, p.y + 0.04, p.z + 0.4, 0.2, new THREE.Vector3(0.16, 0.04, 0.12)));
      this.light(p.clone().setY(p.y + 0.15), INKC, 1.2, 0.7, 0.4);
    }
    // Doors in the other wedges (dark arches: the scribes come in there).
    for (const d of L.doors) {
      const p = this.at(d.x * 1.06, d.y * 1.06);
      const yaw = Math.atan2(p.x - c0.x, p.z - c0.z);
      kit.add(box, '#111214', place(p.x, p.y + 1.0, p.z, yaw, new THREE.Vector3(1.3, 2.0, 0.3)));
      if (!out) this.light(new THREE.Vector3(p.x, p.y + 1.4, p.z), INKC, 1.4, 0.25, 0.5);
    }
    // Embers on the floor all round (out in the close).
    if (!out)
      for (const h of L.floor) {
        if ((h.q * 7 + h.r * 13) % 9 !== 0) continue;
        const p = this.hex(h.q, h.r);
        kit.add(box, '#3a1a10', place(p.x, p.y + 0.03, p.z, h.q, new THREE.Vector3(0.22, 0.05, 0.18)));
        this.light(p.clone().setY(p.y + 0.08), EMBER, 0.7, 0.45, 1);
      }
    box.dispose();
    cyl.dispose();
    if (!out) this.buildFlames(L);
  }

  /** The walls of fire: instanced flames on each spoke's cells, and the red floor under them while warned. */
  private buildFlames(L: CabinetLayout): void {
    this.spokeAt = L.spokes.map((sp) => sp.map((c) => this.hex(c.q, c.r)));
    const n = this.spokeAt.reduce((a, x) => a + x.length, 0);
    this.flameMat = this.track(flameMat());
    const cone = this.track(new THREE.ConeGeometry(0.26, 1, 7, 4, true));
    cone.translate(0, 0.5, 0);
    this.flames = new THREE.InstancedMesh(cone, this.flameMat, n * 2);
    this.flames.frustumCulled = false;
    this.flames.renderOrder = 8;
    this.group.add(this.flames);
    this.redMat = this.track(
      new THREE.MeshBasicMaterial({
        color: '#ff4f3a',
        transparent: true,
        opacity: 0.5,
        depthWrite: false,
      }),
    );
    const disc = this.track(new THREE.CircleGeometry(0.62 * S, 6));
    disc.rotateX(-Math.PI / 2);
    this.floorRed = new THREE.InstancedMesh(disc, this.redMat, n);
    this.floorRed.frustumCulled = false;
    this.group.add(this.floorRed);
  }

  /* ---------- the scenes ---------- */

  /** The stilts of the fin: a pier on piles over the night sea, huts on stilts, the Terca moored, her lanterns. */
  private buildStilts(kit: Kit, dawn: boolean): void {
    this.sea = this.track(seaMat());
    (this.sea.uniforms.dawn as THREE.IUniform<number>).value = dawn ? 1 : 0;
    const water = new THREE.Mesh(this.track(new THREE.PlaneGeometry(160, 160)), this.sea);
    water.rotation.x = -Math.PI / 2;
    this.seaY = -0.55;
    water.position.y = this.seaY;
    this.group.add(water);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 7);
    const WOOD = ['#4a4038', '#3e362f', '#554a40'];
    // The pier: planks over the board's middle, east–west, its piles down into the water.
    for (let i = -5; i <= 5; i++) {
      kit.add(box, WOOD[(i + 9) % 3] as string, place(i * 0.62, 0.0, 0.4, 0, new THREE.Vector3(0.58, 0.1, 2.6)));
      if (i % 2 === 0) for (const z of [-0.85, 1.65]) kit.add(cyl, '#2c2622', place(i * 0.62, -0.6, z, 0, new THREE.Vector3(0.1, 1.3, 0.1)));
    }
    // Huts on stilts behind (north): plank walls, thatch, a lit window.
    for (const [hx, hz, k] of [
      [-4.5, -4.2, 0],
      [-0.8, -5.2, 1],
      [3.6, -4.4, 2],
    ] as const) {
      for (const dx of [-0.9, 0.9]) for (const dz of [-0.7, 0.7]) kit.add(cyl, '#2c2622', place(hx + dx, -0.4, hz + dz, 0, new THREE.Vector3(0.09, 1.8, 0.09)));
      kit.add(box, WOOD[k] as string, place(hx, 0.95, hz, 0.1 * k, new THREE.Vector3(2.2, 1.4, 1.8)));
      kit.add(new THREE.ConeGeometry(1.7, 1.1, 4), '#3a3630', place(hx, 2.15, hz, Math.PI / 4 + 0.1 * k, 1));
      this.light(new THREE.Vector3(hx + 0.4, 1.0, hz + 0.92), '#ffb48a', 0.9, 0.7, 0.3);
    }
    // The Terca, moored to the pier's south: a decked sloop, a tall mast, her red sail furled on the boom, lanterns.
    const tz = 2.6;
    const hull = new THREE.CylinderGeometry(1, 1, 1, 12, 1, false, 0, Math.PI);
    kit.add(hull, '#2f3238', place(0.6, -0.25, tz, Math.PI / 2, new THREE.Vector3(1.05, 5.2, 0.7), 0, Math.PI / 2));
    kit.add(box, '#5a4c40', place(0.6, 0.12, tz, 0, new THREE.Vector3(4.9, 0.08, 1.7)));
    kit.add(box, '#4a1e1c', place(0.6, 0.0, tz, 0, new THREE.Vector3(5.3, 0.12, 1.95)));
    kit.add(cyl, '#3a3029', place(0.9, 2.3, tz, 0, new THREE.Vector3(0.07, 4.4, 0.07)));
    kit.add(cyl, '#3a3029', place(-0.3, 0.9, tz, 0, new THREE.Vector3(0.05, 2.4, 0.05), 0, Math.PI / 2));
    kit.add(cyl, '#8e2a22', place(-0.3, 1.05, tz, 0, new THREE.Vector3(0.16, 2.2, 0.16), 0, Math.PI / 2));
    for (const lx of [-1.6, 2.6]) {
      kit.add(box, IRON, place(lx, 0.45, tz + 0.75, 0, new THREE.Vector3(0.14, 0.2, 0.14)));
      this.light(new THREE.Vector3(lx, 0.48, tz + 0.75), '#ffd0b0', 1.4, 0.8, 0.4);
    }
    // A lantern on the pier, crates, a net drying over a rail.
    kit.add(box, '#4f4438', place(-3.2, 0.3, 0.1, 0.3, new THREE.Vector3(0.6, 0.5, 0.5)));
    kit.add(box, '#463c32', place(-2.4, 0.25, 0.9, -0.2, new THREE.Vector3(0.5, 0.4, 0.5)));
    kit.add(box, '#3c3a38', place(-1.2, 0.5, -0.8, 0, new THREE.Vector3(2.4, 0.04, 0.04)));
    kit.add(box, '#55524c', place(-1.2, 0.35, -0.82, 0, new THREE.Vector3(2.2, 0.3, 0.02)));
    kit.add(cyl, IRON, place(2.2, 0.9, -0.6, 0, new THREE.Vector3(0.04, 1.8, 0.04)));
    this.light(new THREE.Vector3(2.2, 1.85, -0.6), dawn ? COLD : '#ffd0b0', 2.0, dawn ? 0.4 : 0.85, 0.5);
    // The crate the man waits on (the pier at dawn), where he sits.
    if (dawn) {
      const c = this.hex(0, -1);
      kit.add(box, '#4a4036', place(c.x, 0.22, c.z, 0.2, new THREE.Vector3(0.62, 0.44, 0.52)));
    }
    // The page in Isra's hands (by night): an ember red glow by the gunwale.
    if (!dawn) this.light(new THREE.Vector3(0.05, 0.75, 0.45), RED, 1.3, 0.9, 1);
    box.dispose();
    cyl.dispose();
    hull.dispose();
  }

  /** The bastion's yard by the Laboratory at dusk: flags, a brazier, a table of flasks, crates. */
  private buildYard(kit: Kit): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 8);
    kit.add(box, '#3b3430', place(2.6, 0.45, -1.4, 0.3, new THREE.Vector3(1.8, 0.08, 0.8)));
    for (const [x, z] of [
      [1.9, -1.7],
      [3.3, -1.1],
    ] as const)
      kit.add(box, '#2c2622', place(x, 0.22, z, 0.3, new THREE.Vector3(0.08, 0.44, 0.6)));
    for (let i = 0; i < 6; i++) {
      const c = ['#7fb8d8', '#8fd88a', '#d8d0c8', '#b86a5a'][i % 4] as string;
      kit.add(cyl, c, place(2.0 + i * 0.22, 0.6, -1.5 + (i % 2) * 0.25, 0, new THREE.Vector3(0.06, 0.22, 0.06)));
    }
    kit.add(cyl, IRON, place(-2.4, 0.4, 1.4, 0, new THREE.Vector3(0.36, 0.2, 0.36)));
    this.light(new THREE.Vector3(-2.4, 0.75, 1.4), FIREC, 1.8, 0.7, 1);
    kit.add(box, '#4a4038', place(-3.0, 0.3, -1.6, 0.5, new THREE.Vector3(0.6, 0.6, 0.6)));
    kit.add(box, '#3e362f', place(-2.4, 0.25, -2.2, 0.2, new THREE.Vector3(0.5, 0.5, 0.5)));
    // The page on the table, glowing red.
    this.light(new THREE.Vector3(2.4, 0.55, -1.3), RED, 1.0, 0.85, 1);
    box.dispose();
    cyl.dispose();
  }

  /** The catalog: the wall of drawers to the north, shelves round, a lantern on the floor. */
  private buildCatalogRoom(kit: Kit): void {
    this.catalogWall(kit, 0, -3200, 0, 1);
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (const x of [-3.2, 3.2]) {
      kit.add(box, CHAR[0] as string, place(x, 1.0, -1.6, x > 0 ? -0.5 : 0.5, new THREE.Vector3(0.4, 2.0, 2.4)));
      this.heap(kit, x * 0.8, 0.4, 0, x, true);
    }
    kit.add(box, IRON, place(0.6, 0.15, 0.6, 0, new THREE.Vector3(0.14, 0.25, 0.14)));
    this.light(new THREE.Vector3(0.6, 0.3, 0.6), COLD, 2.2, 0.7, 0.4);
    box.dispose();
  }

  /** The collapse's mouth: a slope of beams and stones going down into the dark, a lantern, rope. */
  private buildCollapse(kit: Kit): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    for (let i = 0; i < 9; i++) {
      const a = i * 0.7;
      kit.add(rock, SOOT[i % 4] as string, place(Math.cos(a) * 2.6, 0.3, -2.0 + Math.sin(a) * 0.8, a, 0.9 + (i % 3) * 0.3));
      kit.add(box, CHAR[i % 3] as string, place(-1.5 + i * 0.4, 0.5 + (i % 2) * 0.3, -2.6, 0.3 * i, new THREE.Vector3(0.24, 0.24, 2.2), 0.4, 0.3));
    }
    kit.add(box, IRON, place(1.4, 0.15, 0.9, 0, new THREE.Vector3(0.14, 0.25, 0.14)));
    this.light(new THREE.Vector3(1.4, 0.3, 0.9), COLD, 2.0, 0.7, 0.4);
    // The dark of the way down, a faint red far below (the page's kin).
    this.light(new THREE.Vector3(0, 0.2, -3.2), RED, 2.4, 0.25, 0.5);
    box.dispose();
    rock.dispose();
  }

  /* ---------- the board ---------- */

  apply(terrain: TerrainBuild, hour: Hour = 'night'): void {
    this.terrain = terrain;
    const c = new THREE.Color();
    const inside = this.inside;
    const rock = new Set<string>();
    for (const h of this.map.rocks ?? []) rock.add(`${h.q},${h.r}`);
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    const sea = this.place === 'stilts' || this.place === 'pier';
    if (col && pos) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      for (let i = 0; i < col.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const k = 0.5 + 0.5 * Math.sin(v.x * 1.7 + v.z * 0.9) * Math.cos(v.z * 1.3 - v.x * 0.5);
        // The library's flags grey with ash (darker in streaks), the yard's paler stone.
        // (Under the burnt floor's skin the board is the ash's pale cold grey: the skin darkens it into slabs and soot.)
        const skin = this.floorCells.length > 0;
        c.set((skin ? FLAG_ASH : FLAG)[Math.floor(k * 2.99)] as string).multiplyScalar(skin ? 0.95 + lum * 0.2 : (this.place === 'labYard' ? 1.05 : 0.85) + lum * 0.4);
        if (inside) {
          const hq = Math.round(v.x / S - v.z / (S * 0.866) / 2);
          const hr = Math.round(v.z / (S * 0.866));
          const d = Math.max(Math.abs(hq), Math.abs(hr), Math.abs(hq + hr));
          const scene = this.place === 'catalog' || this.place === 'collapse';
          if (rock.has(`${hq},${hr}`) || d >= this.map.radius - 1 || (scene && d > 5)) c.set('#0c0c0d');
        }
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    terrain.decor.traverse((o) => {
      if (o.name === 'roads') o.visible = false;
      if (o.name === 'outskirts-ground') o.visible = !inside && !sea;
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    // On the water the board is the sea's: the pier is the dressing's.
    if (sea) terrain.mesh.visible = false;
    if (terrain.fog) terrain.fog.group.visible = !inside;
    if (!inside) {
      this.dome = new SkyDome();
      this.group.add(this.dome.mesh);
    }
    this.paint(hour);
  }

  paint(hour: Hour): void {
    const look = HOURS[this.inside ? 'inside' : hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.sea) (this.sea.uniforms.dawn as THREE.IUniform<number>).value = hour === 'dawn' || hour === 'day' ? 1 : 0;
    this.terrain?.fog?.tint({
      low: look.fog[0],
      mid: look.fog[0],
      far: look.bg,
      alpha: 1,
    });
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.at(e.x, e.y, 0.1);
      if (e.kind === 'zap') {
        const h = s.heroes[0];
        if (h) this.ctx.vfx.beam(this.at(h.x, h.y, 1.3), this.at(e.x, e.y, 1.1), '#cfe0ff', 3, 0.05, 0.25);
      } else if (e.kind === 'item') {
        const it = this.items[e.n ?? 0];
        const col = it?.kind === 0 ? '#cfe6ff' : it?.kind === 1 ? '#8fe88a' : '#ffb07a';
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.5), {
          n: 18,
          color: col,
          speed: 2.2,
          up: 1.4,
          size: 0.12,
          life: 10,
          gravity: 1,
        });
        this.ctx.flare(p.clone().setY(p.y + 0.6), col, 1.4, 8);
      } else if (e.kind === 'refill') {
        this.ctx.vfx.ring(p, 0.2, 0.9, '#e8eef8', 8, 0.6);
      } else if (e.kind === 'seal') {
        const what = (e.n ?? 0) % 8;
        const sv = this.seals[Math.floor((e.n ?? 0) / 8)];
        const sp = sv ? sv.at : p;
        if (what === 2) {
          // Broken: shards of ice, a green-orange burst, ink thrown off; the book opens.
          const col = sv?.kind === 0 ? '#d8ecff' : sv?.kind === 1 ? '#a8ff7a' : '#ff3a2a';
          this.ctx.shake(0.4);
          this.ctx.flare(sp, '#ffffff', 3.2, 14);
          this.ctx.vfx.burst(sp, {
            n: 40,
            color: col,
            speed: 3.6,
            up: 2.2,
            size: 0.16,
            life: 16,
            gravity: 1,
          });
          if (sv?.kind === 1)
            this.ctx.vfx.burst(sp, {
              n: 20,
              color: '#ff8a3a',
              speed: 2.4,
              up: 2.6,
              size: 0.22,
              life: 14,
            });
          this.ctx.vfx.ring(sp.clone().setY(sp.y - 0.9), 0.3, 2.6, '#ffffff', 12, 0.8);
        } else if (what === 1) {
          this.ctx.vfx.ring(sp.clone().setY(sp.y - 0.9), 0.2, 1.4, sv?.kind === 0 ? '#8cc4ff' : '#7dff8a', 10, 0.8);
          this.ctx.vfx.burst(sp, {
            n: 10,
            color: sv?.kind === 0 ? '#cfe6ff' : '#8fe88a',
            speed: 1,
            up: 0.8,
            size: 0.14,
            life: 14,
          });
        } else if (what === 4 || what === 5) {
          // Undone or spent: a hiss of steam or smoke.
          this.ctx.vfx.burst(sp, {
            n: 10,
            color: '#9aa0a8',
            speed: 0.6,
            up: 1.6,
            size: 0.4,
            life: 20,
            smoke: true,
          });
        } else
          this.ctx.vfx.burst(sp, {
            n: 6,
            color: '#cfe0ff',
            speed: 1.4,
            up: 0.6,
            size: 0.06,
            life: 6,
          });
      } else if (e.kind === 'reseal') {
        const sv = this.seals[e.n ?? 0];
        if (sv) {
          this.ctx.flare(sv.at, '#ff3a2a', 2.4, 12);
          this.ctx.vfx.ring(sv.at.clone().setY(sv.at.y - 0.9), 2.2, 0.3, '#ff3a2a', 12, 0.9);
        }
        this.ctx.shake(0.25);
      } else if (e.kind === 'write') {
        this.writing.set(e.n ?? 0, this.now);
      } else if (e.kind === 'inked') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.8), {
          n: 30,
          color: '#1a0c10',
          speed: 2.6,
          up: 1.6,
          size: 0.24,
          life: 18,
          smoke: true,
        });
        this.ctx.flare(p.clone().setY(p.y + 0.8), '#ff3a2a', 2.6, 12);
      } else if (e.kind === 'rune') {
        this.runes.push({
          p: p.clone(),
          t0: this.now,
          t1: this.now + (e.n ?? 36) / 12,
        });
      } else if (e.kind === 'runeHit') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.3), {
          n: 16,
          color: '#ff3a2a',
          speed: 1.8,
          up: 1.4,
          size: 0.12,
          life: 10,
        });
      } else if (e.kind === 'spokeUp') {
        const sp = this.spokeAt[e.n ?? 0];
        if (sp)
          for (const q of sp)
            this.ctx.vfx.burst(q.clone().setY(q.y + 0.3), {
              n: 3,
              color: '#ffb07a',
              speed: 1.2,
              up: 2.4,
              size: 0.12,
              life: 10,
            });
      } else if (e.kind === 'caught') {
        this.ctx.shake(0.5);
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.6), {
          n: 24,
          color: '#ff8a3a',
          speed: 2.6,
          up: 2,
          size: 0.16,
          life: 12,
        });
      } else if (e.kind === 'rise') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.3), {
          n: 22,
          color: '#7a7672',
          speed: 1.4,
          up: 1.8,
          size: 0.4,
          life: 22,
          smoke: true,
        });
        this.ctx.flare(p.clone().setY(p.y + 0.6), '#ff4f3a', 1.6, 8);
      } else if (e.kind === 'beams') {
        this.ctx.shake(0.3);
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.4), {
          n: 18,
          color: '#ff8a3a',
          speed: 2,
          up: 1.6,
          size: 0.1,
          life: 12,
          gravity: 1,
        });
      } else if (e.kind === 'flock') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 1.0), {
          n: 14,
          color: '#ff6a2a',
          speed: 1.6,
          up: 1.4,
          size: 0.1,
          life: 14,
        });
      } else if (e.kind === 'opened') {
        // Every ember of the room out at once; only the ink still glows.
        this.embersOut = true;
        if (this.emberMat) (this.emberMat.uniforms.quench as THREE.IUniform<number>).value = 1;
        this.flash = 1;
        this.ctx.shake(0.7);
        this.ctx.flare(p.clone().setY(p.y + 1.4), '#ffffff', 5, 20);
      }
    }
  }

  draw(s: BattleState): void {
    this.lastState = s;
    // The cellars: risen piles flatten; the beams drop over a stair while shut.
    const sw = s.senda?.soot;
    if (sw) {
      for (const pl of this.piles) pl.flat = (sw.risen[pl.hall]?.[pl.k] ?? 0) ? 0.25 : 1;
      this.beams.forEach((b, i) => (b.want = sw.shut[i] ? 0 : 3.2));
    }
    // The locks: each seal whole or broken, each thing there or gone.
    const w = s.senda?.seals;
    if (w) {
      this.seals.forEach((v, i) => {
        const br = !!w.seals[i]?.broken;
        v.shell.visible = !br;
        v.open.visible = br;
      });
      this.items.forEach((it, i) => (it.m.visible = (w.back[i] ?? 0) === 0));
    }
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    if (this.sea) (this.sea.uniforms.time as THREE.IUniform<number>).value = now;
    if (this.flameMat) (this.flameMat.uniforms.time as THREE.IUniform<number>).value = now;
    if (this.emberMat) (this.emberMat.uniforms.time as THREE.IUniform<number>).value = now;
    for (const pl of this.piles) pl.m.scale.y += (pl.flat - pl.m.scale.y) * Math.min(1, dt * 4);
    for (const b of this.beams) {
      b.y += (b.want - b.y) * Math.min(1, dt * 6);
      b.m.position.y = this.ctx.ground(b.m.position.x, b.m.position.z) + b.y;
      b.m.visible = b.y < 3.0;
    }
    const s = this.lastState;
    this.flash = Math.max(0, this.flash - dt * 0.8);
    this.drawFlames(s, now);
    this.glow.begin();
    for (const l of this.lights) {
      // (Out with the embers: the cabinet open, only the cold lamps and the ink.)
      const red = l.c.r > l.c.b + 0.3;
      if (this.embersOut && red && l.c !== INKC && !l.c.equals(INKC)) continue;
      const f = l.flick ? 0.84 + 0.16 * Math.sin(now * (5 + l.seed) + l.seed) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * f);
    }
    const w = s?.senda?.seals;
    if (s && w) {
      // Each seal pulses the colour of what it lacks: frost wanted (blue), lightning on its frost (white), acid (green),
      // fire on its acid (orange), the ink its scribe (red); broken, a soft white.
      const r = s.rules?.senda?.seals;
      this.seals.forEach((v, i) => {
        const st = w.seals[i];
        if (!st || !r) return;
        const kind = r.seals[i]?.kind ?? 0;
        if (st.broken) return this.glow.add(v.at.x, v.at.y + 0.2, v.at.z, COLD, 1.4, 0.45);
        const c = kind === 0 ? (st.prep === 1 ? COLD : FROSTC) : kind === 1 ? (st.prep === 2 ? FIREC : ACIDC) : INKC;
        const fast = (kind === 0 && st.prep === 1) || (kind === 1 && st.prep === 2);
        const pulse = 0.55 + 0.45 * Math.sin(now * (fast ? 7 : 2.6) + i);
        this.glow.add(v.at.x, v.at.y + 0.15, v.at.z, c, fast ? 2.2 : 1.7, 0.35 + 0.5 * pulse);
      });
      // A scribe writing at the desk: its rune grows there (3 s).
      for (const u of w.scribes) {
        if (u.mode !== 1) continue;
        const t0 = this.writing.get(u.id) ?? now;
        const k = Math.min(1, (now - t0) / 3);
        const d = this.at(r?.desk.x ?? 0, r?.desk.y ?? 0, 0.1);
        for (let a = 0; a < 10; a++) {
          const ang = (a / 10) * Math.PI * 2 + now * 0.8;
          this.glow.add(d.x + Math.cos(ang) * (0.6 + 1.6 * k), d.y + 0.08, d.z + Math.sin(ang) * (0.6 + 1.6 * k), INKC, 0.5 + k * 0.6, 0.5 + 0.4 * k);
        }
      }
    }
    // Runes under the hero: a red ring closing in.
    this.runes = this.runes.filter((q) => now < q.t1 + 0.2);
    for (const q of this.runes) {
      const k = Math.min(1, (now - q.t0) / Math.max(0.1, q.t1 - q.t0));
      const rr = 0.95 * (1 - k * 0.5);
      for (let a = 0; a < 12; a++) {
        const ang = (a / 12) * Math.PI * 2 - now * 1.4;
        this.glow.add(q.p.x + Math.cos(ang) * rr, q.p.y + 0.08, q.p.z + Math.sin(ang) * rr, RED, 0.35 + k * 0.3, 0.55 + 0.4 * k);
      }
    }
    // The page in the hero's hand: red, by how near the nearest nightmare is (the cellars).
    const sw = s?.senda?.soot;
    const h = s?.heroes[0];
    if (sw && h && h.downTicks === 0) {
      const p = this.ctx.pos(h.id) ?? this.at(h.x, h.y);
      const k = sw.near ? Math.max(0, 1 - sw.near / 6000) : 0;
      const beat = 0.7 + 0.3 * Math.sin(now * (3 + k * 9));
      this.glow.add(p.x + 0.2, p.y + 1.0, p.z + 0.1, RED, 0.5 + k * 1.6, (0.25 + 0.7 * k) * beat);
    }
    if (this.flash > 0) {
      const c0 = this.at(0, 0, 1.6);
      this.glow.add(c0.x, c0.y, c0.z, COLD, 4 + 6 * this.flash, this.flash);
    }
    this.glow.end();
  }

  /** The walls of fire this frame: the floor red while warned, the flames up while up (rising a moment as they light). */
  private drawFlames(s: BattleState | null, now: number): void {
    const sp = s?.rules?.senda?.seals?.spokes;
    if (!this.flames || !this.floorRed || !sp || !s) return;
    // (Written only when a wall changes: the flicker is the shader's. Every frame they cost a phone ~40 ms/s, docs/RENDIMIENTO.md.)
    const t0 = s.waveT;
    let key = this.embersOut ? -1 : 0;
    for (let k = 0; k < this.spokeAt.length && key >= 0; k++) key = key * 3 + spokeState(sp, k, t0);
    if (this.redMat) this.redMat.opacity = 0.32 + 0.18 * Math.sin(now * 8);
    if (key === this.flameKey) return;
    this.flameKey = key;
    const zero = this.m4.makeScale(0, 0, 0);
    let fi = 0;
    let ri = 0;
    const t = s.waveT;
    for (let k = 0; k < this.spokeAt.length; k++) {
      const st = this.embersOut ? 0 : spokeState(sp, k, t);
      const cells = this.spokeAt[k] as THREE.Vector3[];
      for (let j = 0; j < cells.length; j++) {
        const c = cells[j] as THREE.Vector3;
        if (st >= 1) {
          this.m4.compose(this.v3.set(c.x, c.y + 0.03, c.z), this.q4.identity(), this.s3.set(1, 1, 1));
          this.floorRed.setMatrixAt(ri++, this.m4);
        }
        for (let f = 0; f < 2; f++) {
          if (st === 2) {
            const hgt = 2.2 + (f ? 0.6 : 0) + ((j * 7 + f * 3) % 5) * 0.12;
            this.m4.compose(this.v3.set(c.x + (f ? 0.25 : -0.2), c.y, c.z + (f ? -0.15 : 0.2)), this.q4.identity(), this.s3.set(f ? 0.8 : 1.1, hgt, f ? 0.8 : 1.1));
            this.flames.setMatrixAt(fi++, this.m4);
          }
        }
      }
    }
    const total = this.spokeAt.reduce((a, x) => a + x.length, 0);
    for (let i = fi; i < total * 2; i++) this.flames.setMatrixAt(i, zero);
    for (let i = ri; i < total; i++) this.floorRed.setMatrixAt(i, zero);
    this.flames.instanceMatrix.needsUpdate = true;
    this.floorRed.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    for (const t of this.texs) t.dispose();
    this.dome?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}
